import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "../lib/auth";
import { ArrowRight, Building2, Users } from "lucide-react";

export default function Register() {
    const { registerClient, registerProvider } = useAuth();
    const navigate = useNavigate();
    const [userType, setUserType] = useState("client");
    const [form, setForm] = useState({
        email: "",
        password: "",
        full_name: "",
        phone: "",
        company_name: "",
        contact_name: "",
        contact_phone: "",
    });
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const u = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            if (userType === "client") {
                await registerClient({
                    email: form.email,
                    password: form.password,
                    full_name: form.full_name,
                    phone: form.phone,
                    company_name: form.company_name || null,
                });
                navigate("/dashboard/client");
            } else {
                await registerProvider({
                    email: form.email,
                    password: form.password,
                    company_name: form.company_name,
                    contact_name: form.contact_name,
                    contact_phone: form.contact_phone,
                });
                navigate("/dashboard/provider");
            }
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || "Registration failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="pt-20 min-h-screen bg-zinc-50 pb-16">
            <div className="max-w-md w-full mx-auto mt-6 px-4">
                <div className="bg-white border border-zinc-200 p-8">
                    <div className="text-[10px] uppercase tracking-label text-zinc-500">Flexible Workspace Group</div>
                    <h1 className="font-display text-3xl font-semibold tracking-tight mt-2">Create account</h1>

                    <div className="mt-6 grid grid-cols-2 gap-px bg-zinc-200">
                        <button
                            type="button"
                            data-testid="register-type-client"
                            onClick={() => setUserType("client")}
                            className={`p-3 flex flex-col items-center gap-1 text-[10px] uppercase tracking-label ${userType === "client" ? "bg-zinc-950 text-white" : "bg-white text-zinc-700"}`}
                        >
                            <Users className="h-4 w-4" strokeWidth={1.5} /> I need an address
                        </button>
                        <button
                            type="button"
                            data-testid="register-type-provider"
                            onClick={() => setUserType("provider")}
                            className={`p-3 flex flex-col items-center gap-1 text-[10px] uppercase tracking-label ${userType === "provider" ? "bg-zinc-950 text-white" : "bg-white text-zinc-700"}`}
                        >
                            <Building2 className="h-4 w-4" strokeWidth={1.5} /> I operate a space
                        </button>
                    </div>

                    <form onSubmit={submit} className="mt-5 space-y-4">
                        <div>
                            <label className="text-[10px] uppercase tracking-label text-zinc-500">Email</label>
                            <input data-testid="reg-email" type="email" required value={form.email}
                                onChange={(e) => u("email", e.target.value)}
                                className="w-full border border-zinc-300 px-3 py-2.5 mt-1 focus:border-zinc-950 focus:outline-none" />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-label text-zinc-500">Password</label>
                            <input data-testid="reg-password" type="password" required minLength={6} value={form.password}
                                onChange={(e) => u("password", e.target.value)}
                                className="w-full border border-zinc-300 px-3 py-2.5 mt-1 focus:border-zinc-950 focus:outline-none" />
                            <div className="text-[11px] text-zinc-500 mt-1">Minimum 6 characters</div>
                        </div>

                        {userType === "client" ? (
                            <>
                                <div>
                                    <label className="text-[10px] uppercase tracking-label text-zinc-500">Full name</label>
                                    <input data-testid="reg-full-name" required value={form.full_name}
                                        onChange={(e) => u("full_name", e.target.value)}
                                        className="w-full border border-zinc-300 px-3 py-2.5 mt-1 focus:border-zinc-950 focus:outline-none" />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-[10px] uppercase tracking-label text-zinc-500">Phone</label>
                                        <input data-testid="reg-phone" required value={form.phone}
                                            onChange={(e) => u("phone", e.target.value)}
                                            className="w-full border border-zinc-300 px-3 py-2.5 mt-1 focus:border-zinc-950 focus:outline-none" />
                                    </div>
                                    <div>
                                        <label className="text-[10px] uppercase tracking-label text-zinc-500">Company (optional)</label>
                                        <input data-testid="reg-company-optional" value={form.company_name}
                                            onChange={(e) => u("company_name", e.target.value)}
                                            className="w-full border border-zinc-300 px-3 py-2.5 mt-1 focus:border-zinc-950 focus:outline-none" />
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div>
                                    <label className="text-[10px] uppercase tracking-label text-zinc-500">Company name</label>
                                    <input data-testid="reg-company" required value={form.company_name}
                                        onChange={(e) => u("company_name", e.target.value)}
                                        className="w-full border border-zinc-300 px-3 py-2.5 mt-1 focus:border-zinc-950 focus:outline-none" />
                                </div>
                                <div>
                                    <label className="text-[10px] uppercase tracking-label text-zinc-500">Contact name</label>
                                    <input data-testid="reg-contact-name" required value={form.contact_name}
                                        onChange={(e) => u("contact_name", e.target.value)}
                                        className="w-full border border-zinc-300 px-3 py-2.5 mt-1 focus:border-zinc-950 focus:outline-none" />
                                </div>
                                <div>
                                    <label className="text-[10px] uppercase tracking-label text-zinc-500">Contact phone</label>
                                    <input data-testid="reg-contact-phone" required value={form.contact_phone}
                                        onChange={(e) => u("contact_phone", e.target.value)}
                                        className="w-full border border-zinc-300 px-3 py-2.5 mt-1 focus:border-zinc-950 focus:outline-none" />
                                </div>
                            </>
                        )}

                        {error && (
                            <div data-testid="register-error" className="bg-red-50 border-l-2 border-red-400 p-3 text-xs text-red-900">{error}</div>
                        )}
                        <button data-testid="register-submit-btn" type="submit" disabled={loading}
                            className="w-full group bg-zinc-950 text-white py-4 flex items-center justify-between px-5 hover:bg-[#0044FF] transition-colors disabled:opacity-40">
                            <span className="text-sm font-medium">{loading ? "Creating…" : "Create account"}</span>
                            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
                        </button>
                    </form>

                    <div className="mt-5 text-xs text-zinc-600">
                        Already have an account?{" "}
                        <Link to="/login" data-testid="go-to-login" className="underline underline-offset-4 text-zinc-950">Sign in</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
